import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Select, { components } from "react-select";

import ChartPlot from "../components/plot";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../components/Notifications";

import { TagColorOptions, XlsNormalizefileKey } from "../utils/constantsString";

import { getadvanceSettings, getHeaders } from "../network/cipAPI";
import { LICENSE_PERMISSION_IDS } from "../constants/LicenseManagement.constants";
import { hasEligibleLicensePermission } from "../utils/LicenseUtils";

import {
  storeAdvancedSettings,
  storeSelectedValues,
} from "../Store/TemplateStore/template.action";

import "./index.css";

const isPermeabilityToggleOn = (value) =>
  value === true || value === 1 || value === "1";

const Charts = ({ cleaningResponse, date, cips, threshold, activeStep }) => {
  const { t, i18n } = useTranslation();
  const usePermeabilityTriggerFromStore = useSelector(
    (state) => state.advancedSettings.use_permeability_trigger,
  );
  const userLicenses = useSelector((state) => state.userLicenses);
  const isUsePermeabilityToggleEnabled = hasEligibleLicensePermission(
    userLicenses,
    LICENSE_PERMISSION_IDS.USE_PERMEABILITY,
  );
  const selectedTrain = useSelector((state) => state.selTrain);
  const normalizedCategories = [
    XlsNormalizefileKey.normalizeSaltPassage,
    XlsNormalizefileKey.normalizeDPRack2,
    XlsNormalizefileKey.normalizePermeateFlow,
    XlsNormalizefileKey.normalizeWaterPermeability,
  ];
  const otherCategories = [
    XlsNormalizefileKey.date,
    XlsNormalizefileKey.feedFlowrate,
    XlsNormalizefileKey.concentrateFlowrate,
    XlsNormalizefileKey.permeateFlow,
    XlsNormalizefileKey.feedPressure,
    XlsNormalizefileKey.concentratePressure,
    XlsNormalizefileKey.permeatePressure,
    XlsNormalizefileKey.pressureDrop,
    XlsNormalizefileKey.feedQuality,
    XlsNormalizefileKey.permeateConductivity,
    XlsNormalizefileKey.feedTemp,
  ];
  const [selectedValues, setSelectedValues] = useState(
    useSelector((state) => state.selectedValues),
  );
  const [options, setOptions] = useState(null);
  const [headerOptions, setHeaderOptions] = useState(null);
  const hasInitializedSelectionRef = useRef(false);
  const hasAutoAddedNwpForCurrentOnStateRef = useRef(false);
  const dispatch = useDispatch();

  const getColor = (key, index) => {
    if (key.label === XlsNormalizefileKey.normalizePermeateFlow) {
      return "#00A9E0";
    } else if (key.label === XlsNormalizefileKey.permeateFlow) {
      return "#0000FF";
    } else if (key.label === XlsNormalizefileKey.concentrateFlowrate) {
      return "#4682B4";
    } else if (key.label === XlsNormalizefileKey.feedFlowrate) {
      return "#0818A8";
    } else if (key.label === XlsNormalizefileKey.normalizeDPRack2) {
      return "#58B947";
    } else if (key.label === XlsNormalizefileKey.feedPressure) {
      return "#228B22";
    } else if (key.label === XlsNormalizefileKey.pressureDrop) {
      return "#00A36C";
    } else if (key.label === XlsNormalizefileKey.permeatePressure) {
      return "#40B5AD";
    } else if (key.label === XlsNormalizefileKey.concentratePressure) {
      return "#008080";
    } else if (key.label === XlsNormalizefileKey.permeateConductivity) {
      return "#8B0000";
    } else if (key.label === XlsNormalizefileKey.feedQuality) {
      return "#FF0000";
    } else if (key.label === XlsNormalizefileKey.normalizeSaltPassage) {
      return "#e4001c";
    } else if (key.label === XlsNormalizefileKey.normalizeWaterPermeability) {
      return "#5F259F";
    } else if (key.label === XlsNormalizefileKey.feedTemp) {
      return "#FF9E19";
    } else if (key.label === XlsNormalizefileKey.date) {
      return "#C900E3";
    }
    return TagColorOptions[index % 10].code;
  };

  const getThreshold = (key) => {
    if (threshold) {
      if (Array.isArray(threshold) && threshold[0]) {
        return [threshold[0][key]];
      } else if (threshold[key]) {
        return [threshold[key]];
      }
    }
    return [];
  };

  const NoOptionsMessage = (props) => {
    return (
      <components.NoOptionsMessage {...props}>
        <span className="custom-css-class">{t("ValueStrings.noOption")}</span>
      </components.NoOptionsMessage>
    );
  };

  useEffect(() => {
    const fetchHeader = async () => {
      let usePermeabilityTrigger = usePermeabilityTriggerFromStore;
      try {
        const advancedSettingsResponse =
          await getadvanceSettings(selectedTrain);
        const settings = advancedSettingsResponse?.data?.settings;
        if (settings && settings.use_permeability_trigger != null) {
          usePermeabilityTrigger = settings.use_permeability_trigger;
          dispatch(
            storeAdvancedSettings({
              use_permeability_trigger: usePermeabilityTrigger,
            }),
          );
        }
      } catch (error) {
        createNotification(
          ERROR_NOTIFICATION,
          t("ValueStrings.somethingWentWrong"),
          "",
        );
      }

      let headerResponse = await getHeaders();
      headerResponse = headerResponse?.data;
      if (headerResponse) {
        const normalizedOptions = [];
        const others = [];
        const nwpLabel = XlsNormalizefileKey.normalizeWaterPermeability;
        const isUsePermeabilityEnabled = isPermeabilityToggleOn(
          usePermeabilityTrigger,
        );
        const canDisplayNwp =
          isUsePermeabilityToggleEnabled && isUsePermeabilityEnabled;
        Object.keys(headerResponse).forEach((key) => {
          if (key !== "Date (MM/DD/YYYY)" && key !== "Date") {
            if (normalizedCategories.includes(key)) {
              if (key === nwpLabel && !canDisplayNwp) {
                return;
              }
              normalizedOptions.push({
                label: key,
                value: headerResponse[key],
              });
            } else if (otherCategories.includes(key)) {
              others.push({ label: key, value: headerResponse[key] });
            }
          }
        });

        const options = [];
        options.push({
          label: t("ValueStrings.normalized"),
          options: normalizedOptions,
        });
        options.push({ label: t("ValueStrings.others"), options: others });
        setOptions(options);

        const availableLabels = new Set(
          [...normalizedOptions, ...others].map((opt) => opt.label),
        );

        setSelectedValues((previousSelected = []) => {
          const filteredSelection = (
            Array.isArray(previousSelected) ? previousSelected : []
          ).filter((opt) => availableLabels.has(opt.label));
          const nwpOption = normalizedOptions.find(
            (opt) => opt.label === nwpLabel,
          );

          if (!canDisplayNwp) {
            localStorage.removeItem("nwpDeselected");
            hasAutoAddedNwpForCurrentOnStateRef.current = false;
          }

          let nextSelection =
            !hasInitializedSelectionRef.current &&
            filteredSelection.length === 0
              ? normalizedOptions
              : filteredSelection;
          const hasNwpSelected = nextSelection.some(
            (opt) => opt.label === nwpLabel,
          );

          const shouldAutoAddOnToggleOn =
            canDisplayNwp &&
            !hasAutoAddedNwpForCurrentOnStateRef.current &&
            !localStorage.getItem("nwpDeselected");

          if (shouldAutoAddOnToggleOn && !hasNwpSelected && nwpOption) {
            nextSelection = [...nextSelection, nwpOption];
            hasAutoAddedNwpForCurrentOnStateRef.current = true;
          } else if (shouldAutoAddOnToggleOn) {
            hasAutoAddedNwpForCurrentOnStateRef.current = true;
          }

          hasInitializedSelectionRef.current = true;
          dispatch(storeSelectedValues(nextSelection));
          return nextSelection;
        });
        setHeaderOptions(headerResponse);
      }
    };
    fetchHeader();
  }, [
    dispatch,
    isUsePermeabilityToggleEnabled,
    selectedTrain,
    t,
    usePermeabilityTriggerFromStore,
  ]);

  useEffect(() => {
    if (Array.isArray(selectedValues) && headerOptions !== null) {
      const updatedSelectedValues = selectedValues.map((header) => ({
        ...header,
        value: headerOptions[header.label],
      }));

      setSelectedValues(updatedSelectedValues);
      dispatch(storeSelectedValues(updatedSelectedValues));
    }
  }, [dispatch, headerOptions]);

  const onCheckChange = (value) => {
    const nextSelectedValues = Array.isArray(value) ? value : [];
    const nwpLabel = XlsNormalizefileKey.normalizeWaterPermeability;
    const hasNwpSelected = nextSelectedValues.some(
      (opt) => opt.label === nwpLabel,
    );

    if (isUsePermeabilityToggleEnabled) {
      hasNwpSelected
        ? localStorage.removeItem("nwpDeselected")
        : localStorage.setItem("nwpDeselected", "true");
    }

    setSelectedValues(nextSelectedValues);
    dispatch(storeSelectedValues(nextSelectedValues));
  };

  return (
    <div>
      <div className="tabsContainer">
        {headerOptions !== null && (
          <div>
            <div style={{ minWidth: "400px", marginRight: "20px" }}>
              {/* <div className="descriptionText marginTop10 marginBtm10">
                {t("IdentifiedCIPStrings.chartDescription")}
              </div> */}
              <Select
                id="chart-select"
                options={options}
                name="label"
                value={selectedValues}
                isMulti
                GroupType="cat"
                onChange={onCheckChange}
                placeholder={t("ValueStrings.select")}
                components={{ NoOptionsMessage }}
                getOptionLabel={(option) => option.value}
              />
            </div>
            {Array.isArray(selectedValues) && selectedValues.length > 0 && (
              <div
                style={{
                  display: "flex",
                  marginTop: "10px",
                  justifyContent: "center",
                }}
              >
                {(activeStep === 2 || activeStep === 3) && (
                  <div style={{ color: "red", fontSize: 12 }}>
                    <span
                      style={{
                        fontWeight: "bold",
                        fontSize: "16px",
                        marginRight: 2,
                      }}
                    >
                      {" "}
                      |
                    </span>
                    {t("IdentifiedCIPStrings.dottedLineCleaningAdvisor")}
                  </div>
                )}

                <div style={{ fontSize: 12, marginLeft: 16 }}>
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "16px",
                      marginRight: 2,
                    }}
                  >
                    {" "}
                    |
                  </span>
                  {t("IdentifiedCIPStrings.blackLines")}
                </div>
                <div style={{ color: "red", fontSize: 12, marginLeft: 16 }}>
                  <span
                    style={{ fontWeight: "bold", fontSize: 16, marginRight: 2 }}
                  >
                    {" "}
                    |
                  </span>{" "}
                  {t("IdentifiedCIPStrings.redLine")}
                </div>
              </div>
            )}
            <div
              style={{
                marginTop: "20px",
                maxHeight: "70vh",
                overflowY: "scroll",
              }}
              className=""
            >
              {cleaningResponse &&
                selectedValues &&
                selectedValues.map((key, index) => (
                  <div key={`chart-${key["label"]}-${index}`}>
                    <ChartPlot
                      key={`plot-${key["label"]}-${index}`}
                      title={key["value"]}
                      label={key["label"]}
                      data={cleaningResponse[key["label"]]}
                      color={getColor(key, index)}
                      date={date}
                      cips={cips}
                      i18n={i18n}
                      showCIPs
                      threshold={getThreshold(key["label"])}
                    />
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Charts;
